import React from "react";

// Sample figures for the showcase only; nothing here is the signed-in user's data.
const sales = [
  31200, 34800, 33100, 38900, 42600, 47100, 39800, 36400, 40900, 39200, 44800, 51300, 46900, 48920,
];
const branches = [
  { name: "Riyadh Olaya", value: 12480 },
  { name: "Jeddah Tahlia", value: 9860 },
  { name: "Madinah 1", value: 7240 },
  { name: "Dammam Corniche", value: 5110 },
];
const conversations = [
  {
    question: "What sold best yesterday?",
    answer: "Chicken Shawarma Plate led with SAR 6,240, 18% more than the day before.",
    card: "Top items yesterday",
    format: (value: number) => value.toLocaleString("en-US"),
    bars: [
      { name: "Chicken Shawarma Plate", value: 6240 },
      { name: "Beef Shawarma Wrap", value: 4980 },
      { name: "Falafel Box", value: 3110 },
      { name: "Mint Lemonade", value: 2450 },
    ],
  },
  {
    question: "Which branch grew most this week?",
    answer: "Jeddah Tahlia grew 22% on last week, mostly from delivery orders.",
    card: "Branch growth this week",
    format: (value: number) => `+${value}%`,
    bars: [
      { name: "Jeddah Tahlia", value: 22 },
      { name: "Riyadh Olaya", value: 15 },
      { name: "Madinah 1", value: 9 },
      { name: "Dammam Corniche", value: 4 },
    ],
  },
];
const reduceMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * The panel beside the login form: an S dashboard and an S chat answer take turns every
 * five seconds, so new users see what Foodics BI does before they sign in.
 */
export function LoginShowcase() {
  const [scene, setScene] = React.useState(0);
  React.useEffect(() => {
    if (reduceMotion()) return;
    const timer = setInterval(() => setScene((value) => value + 1), 5000);
    return () => clearInterval(timer);
  }, []);
  const showChat = scene % 2 === 1;

  return (
    <section
      aria-label="What Foodics BI does"
      className="relative m-3 flex min-w-0 flex-1 flex-col gap-1.5 overflow-hidden rounded-[18px] bg-(--s-muted) px-10 pt-10"
    >
      <div key={`heading-${showChat}`} className="motion-safe:animate-[s-login-rise_.4s_ease-out]">
        <h2 className="text-2xl font-medium tracking-tight text-balance">
          {showChat ? "Ask anything about your sales" : "Your sales, explained in seconds"}
        </h2>
        <p className="mt-1.5 mb-5 text-sm text-(--s-muted-fg)">
          {showChat
            ? "The Agent answers with charts from your live Foodics data."
            : "Every branch, every hour, in one dashboard."}
        </p>
      </div>
      <div key={scene} className="min-h-0 flex-1 motion-safe:animate-[s-login-rise_.6s_ease-out]">
        {showChat ? (
          <ChatScene conversation={conversations[Math.floor(scene / 2) % conversations.length]} />
        ) : (
          <DashboardScene />
        )}
      </div>
      <div aria-hidden="true" className="absolute inset-x-0 bottom-4 flex justify-center gap-1.5">
        {[false, true].map((chat) => (
          <span
            key={String(chat)}
            className="h-[3px] w-7 overflow-hidden rounded-full bg-(--s-input)"
          >
            <span
              key={scene}
              className={`block h-full w-0 bg-(--s-fg) ${chat === showChat ? "motion-safe:animate-[s-login-fill_5s_linear_forwards]" : ""}`}
            />
          </span>
        ))}
      </div>
    </section>
  );
}

const icons = {
  metric: "M5 3v10M3 6h4M11 3v10M9 10h4",
  line: "M2 12l4-4 3 2 5-6",
  bar: "M3 13V8M8 13V3M13 13V6",
};
const iconColors = {
  metric: "bg-(--s-metric-icon) text-(--s-metric-icon-fg)",
  line: "bg-(--s-line-icon) text-(--s-line-icon-fg)",
  bar: "bg-(--s-bar-icon) text-(--s-bar-icon-fg)",
};

function TileHeader({ icon, title }: { icon: keyof typeof icons; title: string }) {
  return (
    <div className="flex min-w-0 items-center gap-2 text-[13px]">
      <span
        className={`grid size-[22px] shrink-0 place-items-center rounded-md ${iconColors[icon]}`}
      >
        <svg
          viewBox="0 0 16 16"
          className="size-[13px]"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d={icons[icon]} />
        </svg>
      </span>
      <span className="truncate">{title}</span>
    </div>
  );
}

const tileClass =
  "flex min-w-0 flex-col gap-2.5 rounded-[14px] bg-white px-3.5 pt-3 pb-3.5 shadow-[0_0_0_1px_var(--s-hairline)]";

/** Bars start empty and grow once mounted. */
function useGrown() {
  const [grown, setGrown] = React.useState(reduceMotion());
  React.useEffect(() => {
    const timer = setTimeout(() => setGrown(true), 300);
    return () => clearTimeout(timer);
  }, []);
  return grown;
}

function Bar({
  share,
  grown,
  color = "var(--s-chart-2)",
}: {
  share: number;
  grown: boolean;
  color?: string;
}) {
  return (
    <span className="block h-2 overflow-hidden rounded bg-(--s-muted)">
      <span
        className="block h-full rounded transition-[width] duration-1000 ease-out"
        style={{ width: grown ? `${share * 100}%` : 0, background: color }}
      />
    </span>
  );
}

function Metric({
  title,
  target,
  decimals = 0,
  currency,
  delta,
}: {
  title: string;
  target: number;
  decimals?: number;
  currency?: boolean;
  delta: string;
}) {
  const [value, setValue] = React.useState(reduceMotion() ? target : 0);
  React.useEffect(() => {
    if (reduceMotion()) return;
    const start = performance.now();
    let frame = 0;
    const step = (now: number) => {
      const progress = Math.min(1, (now - start) / 1300);
      setValue(target * (1 - (1 - progress) ** 3));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target]);
  return (
    <div className={`${tileClass} col-span-2`}>
      <TileHeader icon="metric" title={title} />
      <div className="truncate text-[clamp(18px,2vw,26px)] font-medium tracking-tight tabular-nums">
        {currency ? (
          <small className="me-1 text-xs font-normal text-(--s-muted-fg)">SAR</small>
        ) : null}
        {value.toLocaleString("en-US", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}
      </div>
      <div className="text-xs text-(--s-success)">
        ↑ {delta} <span className="text-(--s-muted-fg)">vs yesterday</span>
      </div>
    </div>
  );
}

function DashboardScene() {
  const grown = useGrown();
  const width = 360,
    height = 150,
    min = 24000,
    max = 56000;
  const x = (index: number) => 4 + (index * (width - 8)) / (sales.length - 1);
  const y = (value: number) => height - 14 - ((value - min) / (max - min)) * (height - 24);
  const line =
    "M" + sales.map((value, index) => `${x(index).toFixed(1)},${y(value).toFixed(1)}`).join(" L");
  return (
    <div className="grid grid-cols-6 gap-3">
      <Metric title="Net sales" target={48920} currency delta="12.4%" />
      <Metric title="Orders" target={1284} delta="8.1%" />
      <Metric title="Average check" target={38.1} decimals={1} delta="3.9%" />
      <div className={`${tileClass} col-span-4`}>
        <TileHeader icon="line" title="Net sales, last 14 days" />
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="none"
          className="block h-[170px] w-full overflow-visible"
          role="img"
          aria-label="Net sales, last 14 days"
        >
          {[30000, 40000, 50000].map((tick) => (
            <line
              key={tick}
              x1="0"
              x2={width}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--s-input)"
              strokeDasharray="3 3"
            />
          ))}
          <path
            d={`${line} L${x(sales.length - 1)},${height - 14} L${x(0)},${height - 14} Z`}
            fill="var(--s-chart-2)"
            opacity={0}
            className="animate-[s-login-area_.8s_.9s_ease-out_forwards]"
          />
          <path
            d={line}
            pathLength={1}
            fill="none"
            stroke="var(--s-chart-2)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
            strokeDasharray="1"
            strokeDashoffset="1"
            className="animate-[s-login-draw_1.4s_.15s_ease-out_forwards]"
          />
        </svg>
      </div>
      <div className={`${tileClass} col-span-2`}>
        <TileHeader icon="bar" title="Top branches today" />
        <ul className="flex flex-col gap-3 text-xs">
          {branches.map((branch) => (
            <li key={branch.name} className="flex flex-col gap-1">
              <div className="flex justify-between gap-2 text-(--s-muted-fg)">
                <span className="truncate">{branch.name}</span>
                <b className="font-medium text-(--s-fg) tabular-nums">
                  {branch.value.toLocaleString("en-US")}
                </b>
              </div>
              <Bar share={branch.value / branches[0].value} grown={grown} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ChatScene({ conversation }: { conversation: (typeof conversations)[number] }) {
  const instant = reduceMotion();
  const [typed, setTyped] = React.useState(instant ? conversation.question.length : 0);
  const [answered, setAnswered] = React.useState(instant);
  React.useEffect(() => {
    if (instant) return;
    const timer = setInterval(
      () => setTyped((count) => Math.min(count + 1, conversation.question.length)),
      38,
    );
    const reply = setTimeout(() => setAnswered(true), conversation.question.length * 38 + 1300);
    return () => {
      clearInterval(timer);
      clearTimeout(reply);
    };
  }, [conversation, instant]);
  const typing = typed < conversation.question.length;

  return (
    <div className="flex h-full flex-col gap-3.5 pb-11 text-sm">
      <div className="max-w-[80%] self-end rounded-[14px] bg-(--s-secondary) px-3.5 py-2 motion-safe:animate-[s-login-rise_.25s_ease-out]">
        {conversation.question.slice(0, typed)}
        {typing ? (
          <span className="ms-0.5 inline-block h-[1em] w-[1.5px] bg-(--s-fg) align-[-2px] motion-safe:animate-[s-login-blink_1s_steps(1)_infinite]" />
        ) : null}
      </div>
      {!typing && !answered ? (
        <span className="inline-flex items-center gap-2 text-[13px] text-(--s-muted-fg)">
          <span
            className="size-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent"
            aria-hidden="true"
          />
          Querying your sales…
        </span>
      ) : null}
      {answered ? <Answer conversation={conversation} /> : null}
      <div className="mt-auto flex items-center gap-2.5 rounded-[14px] bg-white py-3 ps-3.5 pe-3 text-[13px] text-(--s-muted-fg) shadow-[0_0_0_1px_var(--s-hairline),0_10px_24px_-16px_oklch(0_0_0/25%)]">
        Ask about sales, branches, items…
        <span className="ms-auto grid size-[26px] place-items-center rounded-lg bg-(--s-secondary) text-(--s-fg)">
          <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
            <path
              d="M7 12V2M3 6l4-4 4 4"
              stroke="currentColor"
              strokeWidth="1.6"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}

function Answer({ conversation }: { conversation: (typeof conversations)[number] }) {
  const grown = useGrown();
  const top = conversation.bars[0].value;
  return (
    <div className="flex flex-col gap-2.5 motion-safe:animate-[s-login-rise_.25s_ease-out]">
      <p>{conversation.answer}</p>
      <div className={tileClass}>
        <TileHeader icon="bar" title={conversation.card} />
        <ul className="flex flex-col gap-2 text-xs text-(--s-muted-fg)">
          {conversation.bars.map((bar, index) => (
            <li
              key={bar.name}
              className="grid grid-cols-[minmax(0,12em)_minmax(0,1fr)_auto] items-center gap-2.5"
            >
              <span className="truncate">{bar.name}</span>
              <Bar
                share={bar.value / top}
                grown={grown}
                color={index === 0 ? "var(--s-chart-1)" : undefined}
              />
              <b className="font-medium text-(--s-fg) tabular-nums">
                {conversation.format(bar.value)}
              </b>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
